import React, { useContext } from 'react';

// Styles & Assets
import styles from './FilterCasesDialog.module.css';
import { ReactComponent as FilterSVG } from '../../../assets/icons/filter-off-fill.svg';

// Hooks
import { useTranslation } from 'react-i18next';

// Context
import FiltersContext from '../../../context/FiltersContext';

// Components
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import RowButtonsFilter from '../../PatientsPage/RowButtonsFilter/RowButtonsFilter';

// Other
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import moment from 'moment';

// Interface
interface Props {
    currentEnvironmentID: number | null;
    currentStartDate: moment.Moment | null;
    currentEndDate: moment.Moment | null;
    modalShow?: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterBallActive?: ((isActive: boolean) => void) | undefined
    onConfirm: (environmentIDs: number | null, startDate: moment.Moment | null, endDate: moment.Moment | null) => void;
}

const FilterCasesDialog: React.FC<Props> = ({
    currentEnvironmentID,
    currentStartDate,
    currentEndDate,
    modalShow,
    setFilterBallActive,
    onConfirm
}) => {

    const { t } = useTranslation();

    const { profiles } = useContext(FiltersContext);

    const [tempEnvironmentID, setTempEnvironmentID] = React.useState<number | null>(currentEnvironmentID);
    const [tempStartDate, setTempStartDate] = React.useState<moment.Moment | null>(currentStartDate);
    const [tempEndDate, setTempEndDate] = React.useState<moment.Moment | null>(currentEndDate);

    const handleChangeEnvironment = (id: number) => {
        setTempEnvironmentID(oldID => oldID === id ? null : id);
    };

    const removeFilters = () => {
        onConfirm(null, null, null);
        setFilterBallActive?.(false)        
        setTempEnvironmentID(null);
        setTempStartDate(null);
        setTempEndDate(null);
    }

    const handleConfirm = () => {
        setFilterBallActive?.(tempEnvironmentID !== null || tempStartDate !== null || tempEndDate !== null);
        onConfirm(tempEnvironmentID, tempStartDate, tempEndDate);
        modalShow?.(false);
    }

    const profilesContent = profiles.map((pz) => ({
        id: pz.id, content: pz.name,
    }));

    const newTheme = createTheme({
        components: {
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        fontSize: '15px',
                        padding: '12px 0 12px 12px !important',
                        fontFamily: 'MontserratRegular'
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: { margin: '0' }
                }
            }
        }
    });


    return (
        <div className='dialog'>
            <div className={styles.topBar} style={{ marginBottom: '8px' }}>
                <div>
                    <h1 className='dialogTitle'>
                        {t('filter')}
                    </h1>
                    <h2 className='dialogSubTitle'>
                        {t('cases_history')}
                    </h2>
                </div>
                <button className={`button ${styles.clearFilters}`} onClick={removeFilters}>
                    {t('remove_filters')} <FilterSVG />
                </button>
            </div>
            <div>
                <RowButtonsFilter
                    text={t('environment')}
                    currentFilters={tempEnvironmentID === null ? [] : [tempEnvironmentID]}
                    handleClick={handleChangeEnvironment}
                    content={profilesContent}
                />
                <div className={styles.datesContainer}>
                    <div>
                        <p className={styles.label}>{t('start_date')}</p>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <ThemeProvider theme={newTheme}>
                                <DatePicker
                                    className={styles.datePicker}
                                    value={tempStartDate}
                                    format='DD-MM-YYYY'
                                    onChange={(d) => setTempStartDate(d)}
                                />
                            </ThemeProvider>
                        </LocalizationProvider>
                    </div>
                    <div>
                        <p className={styles.label}>{t('end_date')}</p>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <ThemeProvider theme={newTheme}>
                                <DatePicker
                                    className={styles.datePicker}
                                    value={tempEndDate}
                                    format='DD-MM-YYYY'
                                    onChange={(d) => setTempEndDate(d)}
                                />
                            </ThemeProvider>
                        </LocalizationProvider>
                    </div>
                </div>
            </div>
            <CancelConfirmButtons
                onCancel={() => modalShow?.(false)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default FilterCasesDialog;